import React from 'react';
import {
  FiZap,
  FiCheckCircle,
  FiEdit3,
  FiFileText,
  FiCpu,
} from 'react-icons/fi';

import type { TrainingScenarioType } from '../../services/agentTrainingApi';

// ─── Types ────────────────────────────────────────────────────────────────

interface TrainStep1Props {
  /** Chamado quando o usuário escolhe o tipo */
  onSelect: (type: TrainingScenarioType | 'compare') => void;
  /** Se o tipo custom deve ser exibido (precisa ter dados do agente) */
  allowCustom: boolean;
}

/**
 * Step 1 — Cliente escolhe o tipo de treinamento.
 *
 * 4 opções:
 * 1. **Quick** (5 cenários) — recomendado pra iteração rápida
 * 2. **Full** (12 cenários) — cobertura completa
 * 3. **Custom** — cenários a partir de conversas reais (opcional)
 * 4. **Manual** — só ajuste de prompt, sem rodar testes
 * 5. **Compare** — A/B Testing entre modelos
 */
export const TrainStep1_ChooseType: React.FC<TrainStep1Props> = ({
  onSelect,
  allowCustom,
}) => {
  return (
    <div>
      <div className="mb-5">
        <h2
          className="text-lg font-semibold mb-1"
          style={{ color: 'var(--theme-card-text)' }}
        >
          Como você quer treinar o agente?
        </h2>
        <p
          className="text-sm"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          Escolha o tipo de treinamento. Você pode combinar sugestões
          automáticas com ajustes manuais no próximo passo.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <OptionCard
          icon={<FiZap className="h-6 w-6" />}
          badge="Recomendado"
          title="Treino rápido"
          description="5 cenários essenciais de conversação. Aproximadamente 2 minutos."
          onClick={() => onSelect('quick')}
        />
        <OptionCard
          icon={<FiCheckCircle className="h-6 w-6" />}
          title="Treino completo"
          description="12 cenários cobrindo casos edge. Aproximadamente 4 minutos."
          onClick={() => onSelect('full')}
        />
        {allowCustom && (
          <OptionCard
            icon={<FiFileText className="h-6 w-6" />}
            title="Treino personalizado"
            description="Escreva cenários customizados baseados em conversas reais do seu agente."
            onClick={() => onSelect('custom')}
          />
        )}
        <OptionCard
          icon={<FiEdit3 className="h-6 w-6" />}
          title="Só ajuste manual"
          description="Pula os testes automáticos e vai direto pra editar o prompt com instruções livres."
          onClick={() => onSelect('manual')}
        />

        {/* Comparar Modelos (A/B Testing) */}
        <OptionCard
          icon={<FiCpu className="h-6 w-6" />}
          badge="Novo"
          title="Comparar Modelos"
          description="Teste o mesmo prompt com diferentes modelos e temperaturas. Veja qual entrega melhor score."
          onClick={() => onSelect('compare')}
        />
      </div>
    </div>
  );
};

interface OptionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  badge?: string;
  onClick: () => void;
}

const OptionCard: React.FC<OptionCardProps> = ({
  icon,
  title,
  description,
  badge,
  onClick,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-lg border p-4 transition-colors group"
      style={{
        backgroundColor: '#ffffff',
        borderColor: 'rgba(0, 0, 0, 0.08)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#8b5cf6';
        e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.02)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
        e.currentTarget.style.backgroundColor = '#ffffff';
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex-shrink-0 p-2 rounded-md"
          style={{
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            color: '#8b5cf6',
          }}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
            {badge && (
              <span
                className="text-xs px-1.5 py-0.5 rounded font-medium"
                style={{
                  backgroundColor: '#8b5cf6',
                  color: '#ffffff',
                }}
              >
                {badge}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">{description}</p>
        </div>
      </div>
    </button>
  );
};
